import { normalize } from "./normalize.ts";

export interface Coordinate { lon: number; lat: number }
export interface Country {
  id: string;
  name: string;
  aliases: string[];
  continent: string;
  region: string;
  centroid: Coordinate;
  answerable: boolean;
  selectable: boolean;
}

interface SearchKey { key: string; id: string; rank: number }

const queryKey = (input: string): string => {
  const q = normalize(input);
  return q.startsWith("the ") ? q.slice(4) : q;
};

/** The app's GeoData: countries, the distance matrix and name search. */
export class GeoData {
  readonly countries: Country[];
  private readonly byId: Map<string, number>;
  private readonly km: number[][];
  private readonly names = new Map<string, string>();
  private readonly searchKeys: SearchKey[] = [];

  constructor(countries: Country[], distances: { ids: string[]; km: number[][] }) {
    if (distances.ids.join() !== countries.map((c) => c.id).join()) throw new Error("Distance matrix doesn't match the countries.");
    this.countries = countries;
    this.byId = new Map(countries.map((c, i) => [c.id, i]));
    this.km = distances.km;
    for (const country of countries) {
      if (!country.selectable) continue;
      for (const [raw, rank] of [[country.name, 0], ...country.aliases.map((a) => [a, 1])] as [string, number][]) {
        const key = normalize(raw);
        if (key === "") continue;
        const existing = this.names.get(key);
        if (existing !== undefined && existing !== country.id) throw new Error(`Duplicate name ${key}`);
        if (existing === undefined) this.searchKeys.push({ key, id: country.id, rank });
        this.names.set(key, country.id);
      }
    }
  }

  country(id: string): Country | undefined {
    const i = this.byId.get(id);
    return i === undefined ? undefined : this.countries[i];
  }

  distanceKm(a: string, b: string): number | undefined {
    const i = this.byId.get(a), j = this.byId.get(b);
    return i === undefined || j === undefined ? undefined : this.km[i]![j];
  }

  /** Exact match on a normalized name or alias. Only selectable countries match. */
  match(input: string): Country | undefined {
    const id = this.names.get(queryKey(input));
    return id === undefined ? undefined : this.country(id);
  }

  /** Name or alias starting with the input, or with a word that does. Exact, own name, alias, word inside; then by name. */
  suggestions(input: string, limit = 6): Country[] {
    const q = queryKey(input);
    if (q === "" || limit <= 0) return [];
    const best = new Map<string, number>();
    for (const e of this.searchKeys) {
      let score: number;
      if (e.key === q) score = -1;
      else if (e.key.startsWith(q)) score = e.rank;
      else if (e.key.includes(" " + q)) score = 2;
      else continue;
      best.set(e.id, Math.min(best.get(e.id) ?? Infinity, score));
    }
    return [...best]
      .map(([id, score]) => [score, this.country(id)!] as const)
      .sort((a, b) => (a[0] !== b[0] ? a[0] - b[0] : a[1].name < b[1].name ? -1 : a[1].name > b[1].name ? 1 : 0))
      .slice(0, limit)
      .map(([, c]) => c);
  }

  /** The normalised mean of a continent's centroids, as the app's continentCentre. */
  continentCentre(continent: string): Coordinate | undefined {
    const rad = Math.PI / 180;
    let x = 0, y = 0, z = 0, n = 0;
    for (const c of this.countries) {
      if (c.continent !== continent) continue;
      const lon = c.centroid.lon * rad, lat = c.centroid.lat * rad;
      x += Math.cos(lat) * Math.cos(lon);
      y += Math.cos(lat) * Math.sin(lon);
      z += Math.sin(lat);
      n++;
    }
    const len = Math.hypot(x, y, z);
    if (n === 0 || len <= 1e-9) return undefined;
    return { lon: (Math.atan2(y, x) * 180) / Math.PI, lat: (Math.asin(Math.max(-1, Math.min(1, z / len))) * 180) / Math.PI };
  }
}
